import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import {
  errorText,
  opsApi,
  readSelectedWarehouse,
  writeSelectedWarehouse,
  type OpsWarehouse,
  type SelectedWarehouse,
} from "@/lib/warehouseOps";

/**
 * Màn đầu của máy cầm tay: chọn kho, rồi chọn một trong ba việc làm tại chỗ.
 *
 * Chọn kho trước vì mọi thao tác sau đó đều ghi vào đúng kho đó — ô chứa, tồn
 * kho, phiếu xuất đều thuộc về một kho cụ thể. Kho đã chọn được nhớ lại để
 * người làm cả ca trong một kho không phải chọn lại sau mỗi lần mở máy.
 *
 * Máy cầm tay chỉ có ba việc này. Các nghiệp vụ khác (lập phiếu, duyệt, nhận
 * hàng theo đơn mua) làm trên máy tính.
 */

type Activity = {
  path: string;
  icon: string;
  title: string;
  desc: string;
  badge: (w: OpsWarehouse) => string;
  tone: string;
};

const ACTIVITIES: Activity[] = [
  {
    path: "/warehouse-ops/putaway",
    icon: "ri-archive-drawer-line",
    title: "Đưa hàng vào vị trí",
    desc: "Quét ô trên kệ rồi chọn hàng cần cất vào ô đó",
    badge: (w) => (w.putawayLines > 0 ? `${w.putawayLines} dòng chờ cất` : "Không còn dòng chờ"),
    tone: "bg-brand-50 text-brand-700",
  },
  {
    path: "/warehouse-ops/pick",
    icon: "ri-shopping-basket-2-line",
    title: "Lấy hàng để xuất",
    desc: "Quét đúng ô ghi trên phiếu xuất rồi xác nhận từng dòng",
    badge: (w) => (w.pickNotes > 0 ? `${w.pickNotes} phiếu chờ lấy` : "Không có phiếu chờ"),
    tone: "bg-amber-50 text-amber-700",
  },
  {
    path: "/warehouse-ops/relocate",
    icon: "ri-swap-box-line",
    title: "Luân chuyển nội bộ",
    desc: "Quét ô nguồn, chọn hàng, quét ô đích — trong kho hoặc sang kho khác",
    badge: () => "Quét ô để bắt đầu",
    tone: "bg-slate-100 text-slate-600",
  },
];

export default function WarehouseOpsHomePage() {
  const navigate = useNavigate();
  const { user, getToken, logout } = useAuth();
  const company = useCompany(getToken());

  const [warehouses, setWarehouses] = useState<OpsWarehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<SelectedWarehouse | null>(() => readSelectedWarehouse());
  /** Đang mở danh sách để đổi sang kho khác. */
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await opsApi.warehouses(getToken() ?? undefined);
      setWarehouses(res.data ?? []);
    } catch (e) {
      setError(errorText(e, "Không tải được danh sách kho."));
      setWarehouses([]);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  /*
   * Kho đã nhớ nhưng không còn trong danh sách (ngừng hoạt động, bị xoá) thì
   * mở lại màn chọn kho — im lặng giữ một kho không còn dùng được sẽ khiến mọi
   * thao tác sau đó báo lỗi mà không rõ vì sao.
   */
  useEffect(() => {
    if (loading || error || !selected || warehouses.length === 0) return;
    if (warehouses.some((w) => w.id === selected.id)) return;

    writeSelectedWarehouse(null);
    setSelected(null);
  }, [loading, error, selected, warehouses]);

  const current = useMemo(
    () => warehouses.find((w) => w.id === selected?.id) ?? null,
    [warehouses, selected],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return warehouses;

    return warehouses.filter((w) =>
      [w.code, w.name, w.fullName, w.address].some((v) => (v ?? "").toLowerCase().includes(q)),
    );
  }, [warehouses, search]);

  const choose = (w: OpsWarehouse) => {
    const picked = { id: w.id, code: w.code, name: w.name };
    writeSelectedWarehouse(picked);
    setSelected(picked);
    setSwitching(false);
  };

  const header = (
    <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {company.logoUrl && (
            <img src={company.logoUrl} alt={company.name} className="w-9 h-9 object-contain rounded shrink-0" />
          )}
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 truncate">{company.shortName || company.name}</p>
            <p className="text-xs text-slate-500 truncate">Kho &middot; {user?.name}</p>
          </div>
        </div>
        <button
          onClick={() => { void logout(); navigate("/"); }}
          className="text-xs text-slate-500 px-3 py-1.5 rounded-lg bg-slate-100 shrink-0"
        >
          Đăng xuất
        </button>
      </div>
    </header>
  );

  // ─── Chọn kho ──────────────────────────────────────────────────────────────
  if (!selected || switching) {
    return (
      <div className="min-h-screen bg-slate-50 pb-8">
        {header}

        <div className="p-4 space-y-3">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Chọn kho đang làm việc</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Mọi thao tác sau đó đều ghi vào kho này.
            </p>
          </div>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo mã hoặc tên kho..."
            className="w-full px-3 py-2.5 text-sm border-2 border-slate-200 rounded-lg focus:outline-none focus:border-brand-500"
          />

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="flex-1">{error}</span>
              <button onClick={() => void load()} className="font-medium text-red-700">Tải lại</button>
            </div>
          )}

          {loading ? (
            <p className="text-center text-slate-400 py-12">Đang tải danh sách kho...</p>
          ) : visible.length === 0 ? (
            <p className="text-center text-slate-400 py-12">Không có kho nào.</p>
          ) : (
            visible.map((w) => (
              <button
                key={w.id}
                onClick={() => choose(w)}
                className={`w-full text-left bg-white rounded-xl border p-4 active:bg-slate-50 ${
                  w.id === selected?.id ? "border-brand-400 ring-1 ring-brand-200" : "border-slate-200"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold text-slate-900 truncate">{w.fullName || w.name}</p>
                  <span className="shrink-0 font-mono text-[11px] text-slate-400">{w.code}</span>
                </div>
                {w.address && <p className="text-xs text-slate-500 mt-0.5 truncate">{w.address}</p>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">
                    {w.putawayLines} dòng chờ cất
                  </span>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                    {w.pickNotes} phiếu chờ lấy
                  </span>
                </div>
              </button>
            ))
          )}

          {switching && selected && (
            <button
              onClick={() => setSwitching(false)}
              className="w-full py-3 text-sm text-slate-500"
            >
              Giữ nguyên kho {selected.name}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Chọn việc ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 pb-8">
      {header}

      <div className="p-4 space-y-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Đang làm việc tại kho</p>
          <p className="text-lg font-semibold text-slate-900 mt-0.5 truncate">
            {current?.fullName || selected.name}
          </p>
          <div className="flex items-center justify-between gap-2 mt-1">
            <span className="font-mono text-xs text-slate-400">{selected.code}</span>
            <button
              onClick={() => { setSearch(""); setSwitching(true); }}
              className="text-sm font-medium text-brand-700 px-3 py-1.5 rounded-lg bg-brand-50"
            >
              Đổi kho
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <span className="flex-1">{error}</span>
            <button onClick={() => void load()} className="font-medium text-red-700">Tải lại</button>
          </div>
        )}

        <div className="space-y-3">
          {ACTIVITIES.map((a) => (
            <button
              key={a.path}
              onClick={() => navigate(a.path)}
              className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 active:bg-slate-50 flex items-start gap-3"
            >
              <span className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-xl ${a.tone}`}>
                <i className={a.icon} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-900">{a.title}</span>
                <span className="block text-xs text-slate-500 mt-0.5">{a.desc}</span>
                <span className="inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                  {current ? a.badge(current) : loading ? "Đang đếm..." : "—"}
                </span>
              </span>
              <span className="text-slate-300 text-xl shrink-0">&rsaquo;</span>
            </button>
          ))}
        </div>

        <p className="text-xs text-slate-400 text-center pt-2">
          Mọi thao tác đều bắt đầu bằng quét mã vị trí trên kệ.
        </p>
      </div>
    </div>
  );
}
